import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Card } from './ui/card';

const PrivacyPolicy = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white p-8">
        <div className="flex items-center mb-6">
          <button 
            onClick={onClose}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Website
          </button>
        </div>

        <div className="prose max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Information We Collect</h2>
              <p className="text-gray-600 mb-4">
                We collect information you provide directly to us, such as when you:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Schedule an appointment or consultation</li>
                <li>Fill out contact forms on our website</li>
                <li>Communicate with us via email or phone</li>
                <li>Participate in our therapy sessions or programs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Types of Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Personal Information</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Name and contact information</li>
                    <li>Date of birth and age</li>
                    <li>Emergency contact details</li>
                    <li>Insurance information</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Health Information</h3>
                  <ul className="list-disc pl-6 text-gray-600 space-y-1">
                    <li>Mental health history</li>
                    <li>Treatment records and notes</li>
                    <li>Assessment results</li>
                    <li>Progress reports</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Provide psychological and therapeutic services</li>
                <li>Schedule and manage appointments</li>
                <li>Communicate with you about your treatment</li>
                <li>Maintain treatment records and progress</li>
                <li>Process payments and insurance claims</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Confidentiality & HIPAA Compliance</h2>
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <p className="text-gray-700">
                  <strong>We are committed to protecting your privacy.</strong> All therapeutic communications 
                  and health information are protected under HIPAA (Health Insurance Portability and 
                  Accountability Act) regulations.
                </p>
              </div>
              <p className="text-gray-600 mb-4">
                We will not disclose your information without your written consent, except in the following circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>When there is imminent danger to yourself or others</li>
                <li>In cases of suspected child abuse or elder abuse</li>
                <li>When required by court order or legal proceedings</li>
                <li>For insurance billing and payment processing</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Data Security</h2>
              <p className="text-gray-600 mb-4">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Encrypted data transmission and storage</li>
                <li>Secure, password-protected systems</li>
                <li>Limited access to authorized personnel only</li>
                <li>Regular security audits and updates</li>
                <li>Secure disposal of physical and electronic records</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Your Rights</h2>
              <p className="text-gray-600 mb-4">
                Under HIPAA and applicable privacy laws, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Access your health information and treatment records</li>
                <li>Request corrections to your health information</li>
                <li>Request restrictions on how your information is used</li>
                <li>Request alternative means of communication</li>
                <li>File a complaint if you believe your privacy rights have been violated</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Online Sessions & Technology</h2>
              <p className="text-gray-600 mb-4">
                For online consultations and sessions:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>We use secure, HIPAA-compliant video platforms</li>
                <li>Sessions are not recorded without explicit consent</li>
                <li>We recommend using private, secure internet connections</li>
                <li>Technical safeguards are in place to protect transmission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibent text-gray-800 mb-4">Contact Information</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>For privacy-related questions or concerns:</strong>
                </p>
                <p className="text-gray-600">
                  Mr. Manish Pacher<br/>
                  Clinical Psychologist & Family Therapist<br/>
                  Email: manish.pacher@gmail.com<br/>
                  Phone: +91 9167250014
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Updates to This Policy</h2>
              <p className="text-gray-600">
                We may update this privacy policy from time to time. Any changes will be posted on our website 
                and will be effective immediately upon posting. We encourage you to review this policy 
                periodically to stay informed about how we protect your information.
              </p>
              <p className="text-sm text-gray-500 mt-4">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </section>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PrivacyPolicy;